import { BodyPrimary, H2, H3 } from '@liveauctioneers/hammer-ui-core/text';
import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { Catalog } from '@/types/Catalog';
import { FormattedMessage, useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { getImageUrl, getItemUrlFromSlug } from '@/utils/urls';
import { Link } from '@liveauctioneers/hammer-ui-core/link';
import { shortFormatDate } from '@liveauctioneers/caterwaul-components/lib/utils/dates';
import PageTitle from '@/components/PageTitle/PageTitle';
import React from 'react';
import ReviewPageHeaderMeta from './ReviewPageHeaderMeta';
import styled from 'styled-components';
import type { ItemSummary } from '@/types/item/ItemSummary';

type Props = {
    baseUrl: string;
    catalog: Catalog;
    handleNotReceivedItemsClicked: () => void;
    handleReceivedItemsClicked: () => void;
    items: ItemSummary[];
    receivedItems: boolean;
    selectedRecievedItems: boolean;
};

const ReviewPageHeader = (props: Props) => {
    const { formatMessage } = useIntl();
    const mapImages = React.useCallback((item: ItemSummary): React.ReactElement => {
        const { catalogId, imageVersion, itemId, photos, sellerId, slugWithLocation, title } = item;
        const imageOptions: any = {
            catalogId,
            height: 240,
            imageNumber: photos?.[0],
            imageVersion,
            itemId,
            sellerId,
        };

        const url = getImageUrl(imageOptions);
        const itemUrl = getItemUrlFromSlug(itemId, slugWithLocation);
        return (
            <ImageWrapper key={itemId}>
                <Link
                    href={itemUrl}
                    key={itemId}
                    rel="noopener"
                    target="_blank"
                >
                    <img
                        alt={title}
                        key={itemId}
                        src={url}
                    />
                </Link>
            </ImageWrapper>
        );
    }, []);

    const {
        baseUrl,
        catalog,
        handleNotReceivedItemsClicked,
        handleReceivedItemsClicked,
        items,
        receivedItems,
        selectedRecievedItems,
    } = props;
    const { saleStartTs, sellerName: name, title } = catalog;

    const startDate = shortFormatDate(saleStartTs);
    return (
        <div>
            <ReviewPageHeaderMeta baseUrl={baseUrl} />
            <CenteredPageTitle title={<FormattedMessage id="review_page.review_your_auction" />} />
            <div>
                <H2>{name}</H2>
                <BodyPrimary>{title}</BodyPrimary>
                <br />
                {startDate}
            </div>
            <ImagesContainer>{items.map(mapImages)}</ImagesContainer>
            <CtaContainer>
                <H3>
                    {items.length > 1 ? (
                        <FormattedMessage id="review_page.received_items" />
                    ) : (
                        <FormattedMessage id="review_page.received_item" />
                    )}
                </H3>
                <RecievedItemsButtonContainer>
                    <InlineButton
                        onClick={handleReceivedItemsClicked}
                        variant={selectedRecievedItems && receivedItems ? 'filled' : 'outlined'}
                    >
                        {formatMessage({ id: 'review_page.yes' })}
                    </InlineButton>
                    <InlineButton
                        onClick={handleNotReceivedItemsClicked}
                        variant={selectedRecievedItems && !receivedItems ? 'filled' : 'outlined'}
                    >
                        {formatMessage({ id: 'review_page.no' })}
                    </InlineButton>
                </RecievedItemsButtonContainer>
            </CtaContainer>
        </div>
    );
};

export default ReviewPageHeader;

const RecievedItemsButtonContainer = styled.div`
    margin-top: 12px;
    display: flex;
    button {
        margin-right: 20px;
    }
`;

const CtaContainer = styled.div`
    margin-top: 40px;
`;

const CenteredPageTitle = styled(PageTitle)`
    text-align: center;
`;

const ImagesContainer = styled.div`
    display: flex;
    flex-direction: row;
    margin-top: 15px;
`;

const InlineButton = styled(Button)`
    display: inline-flex;
`;

const ImageWrapper = styled.div`
    height: 120px;
    width: 120px;
    margin-right: 7px;
    border: 1px solid ${({ theme }) => theme.colors.grey300};
    position: relative;

    img {
        max-height: 100%;
        max-width: 100%;
        width: auto;
        height: auto;
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        right: 0;
        margin: auto;
    }
`;
